import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {InboxesFormatter} from '../../../common/formatters/inboxesFormatter';
import {TeammatesFormatter} from '../../../common/formatters/teammatesFormatter';

/*
 * Props.
 */

interface CommentComposerVisibilityProps {
  className?: string;
  significantTeammates: ReadonlyArray<TeammateModel>;
  significantInboxes: ReadonlyArray<InboxModel>;
  userId: number;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  ${ellipsis()};
`;

const StyledCountDiv = styled.div`
  display: inline-block;
`;

/*
 * Component.
 */

export const CommentComposerVisibility: FC<CommentComposerVisibilityProps> = (props) => (
  <StyledDiv className={props.className}>{renderVisibilityText(props)}</StyledDiv>
);

function renderVisibilityText(props: CommentComposerVisibilityProps) {
  const {significantInboxes, significantTeammates, userId} = props;

  // The conversation is in a team inbox.
  if (significantInboxes.length > 0) {
    const formattedInboxes = (
      <InboxesFormatter
        inboxes={significantInboxes}
        limit={2}
        renderInboxName={(name) => <strong>{name}</strong>}
      />
    );
    return (
      <Trans id="commentcomposer-visibility-inboxes">
        Comment will be visible to teammates in {formattedInboxes}
      </Trans>
    );
  }

  // The conversation is shared with teammates.
  if (significantTeammates.length > 1) {
    const formattedTeammates = (
      <TeammatesFormatter
        teammates={significantTeammates}
        userId={userId}
        renderTeammateName={(name) => <strong>{name}</strong>}
        limit={3}
        renderOthersText={(othersText, othersNames) => (
          <StyledCountDiv>
            <TooltipCoordinator render={() => othersNames}>
              <strong>{othersText}</strong>
            </TooltipCoordinator>
          </StyledCountDiv>
        )}
      />
    );
    return (
      <Trans id="commentcomposer-visibility-teammates">Comment will be visible to {formattedTeammates}</Trans>
    );
  }

  // The conversation is private.
  return (
    <Trans id="commentcomposer-visibility-private">
      Comment will be visible to <strong>only you</strong>
    </Trans>
  );
}
